import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTheme } from 'styled-components';
import moment from 'moment';
import uuidTime from 'uuid-time';
import {
  List,
  ListBody,
  ListHeader,
  ListHeaderItem,
  ListItemColumn,
  ListItem,
  MaterialIcon,
} from '@pearsontechnology/aero-components';

import meShape from 'Workspace/types/me';
import { selectCurrentMe } from 'data/CurrentUser/selectors';
import projectServiceShape from 'services/types/projectService';
import enhanceWithServices from 'hocs/enhanceWithServices';
import PlaceholderList from 'components/PlaceholderList';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import messages from './messages';
import { STATUS, INFOTYPE } from './constants';
import ImportListItemDeleteConfirm from '../ImportListItemDeleteConfirm';
import ImportProgressIndicator from '../ImportProgressIndicator';
import { StyledButton } from './styles';
import { getIngestionListItemType } from 'components/Import/ImportList/utils';

const ImportList = ({ ingestionList, projectService, currentUser, supportType, ingestionType }) => {
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [ingestionId, setIngestionId] = useState();

  const onDismiss = () => setShowDeleteConfirmModal(false);

  const getAccountFullName = (currentUser) => `${currentUser?.givenName || ''} ${currentUser?.familyName || ''}`.trim();

  const isFinalState = (status, creator) =>
    status !== STATUS.COMPLETED.text &&
    status !== STATUS.FAILED.text &&
    getAccountFullName(creator) === getAccountFullName(currentUser);

  const handleDeleteIngestion = () => {
    projectService.deleteIngestion(ingestionId);
    onDismiss();
  };

  const importDataFeatures = ingestionList?.map((listData) => {
    const ingestionStats = JSON.parse(listData?.ingestionStats ?? '{}');
    const ingestionStatsCount = Object.keys(ingestionStats)
      .reduce((sum, key) => sum + parseFloat(ingestionStats[key] || 0), 0);
    const { status, creator } = listData;
    return {
      chipStatusColor: STATUS[status]?.color,
      infoType:
        ingestionStatsCount > 0 &&
        status === STATUS.COMPLETED.text
          ? INFOTYPE.WARN
          : INFOTYPE.FAIL,
      displayChip:
        status === STATUS.COMPLETED.text ||
        status === STATUS.FAILED.text,
      deleteIndicator: isFinalState(status, creator),
      ingestionStats,
      ingestionStatsCount,
    };
  });
  return ingestionList === null || ingestionList === [] ? (
    <PlaceholderList />
  ) : (
    <>
      <List>
        <ListHeader>
          <ListItemColumn colWidth="30%" colWidthMobile="100%">
            <ListHeaderItem>{intl.formatMessage(messages.name)}</ListHeaderItem>
          </ListItemColumn>
          <ListItemColumn colWidthMobile="50%">
            <ListHeaderItem>{intl.formatMessage(messages.status)}</ListHeaderItem>
          </ListItemColumn>
          <ListItemColumn colWidthMobile="50%">
            <ListHeaderItem>
              {intl.formatMessage(messages.importBy)}
            </ListHeaderItem>
          </ListItemColumn>
          <ListItemColumn colWidth="20%" colWidthMobile="50%">
            <ListHeaderItem>
              {intl.formatMessage(messages.importDate)}
            </ListHeaderItem>
          </ListItemColumn>
          <ListItemColumn colWidth="10px" colWidthMobile='30%'/>
        </ListHeader>

        <ListBody>
          {ingestionList.map((ingestionListItem, index) => (
            <ListItem key={ingestionListItem.id}>
              <ListItemColumn colWidth="30%" colWidthMobile="100%">
                {ingestionListItem.courseName}
              </ListItemColumn>
              <ListItemColumn colWidthMobile="50%">
                <ImportProgressIndicator
                  {...importDataFeatures[index]}
                  status={ingestionListItem.status}
                  courseName={ingestionListItem.courseName}
                  ingestionId={ingestionListItem.id}
                  ingestionType={getIngestionListItemType(ingestionListItem, ingestionType)}
                />
              </ListItemColumn>
              <ListItemColumn colWidthMobile="50%">
                {ingestionListItem.creator.givenName} {ingestionListItem.creator.familyName}
              </ListItemColumn>
              <ListItemColumn colWidth="20%" colWidthMobile="50%">
                {moment(uuidTime.v1(ingestionListItem.id)).format('D MMMM YYYY h:mm A')}
              </ListItemColumn>
              <ListItemColumn colWidth="10px" colWidthMobile="30%">
                {importDataFeatures[index].deleteIndicator && (
                  <StyledButton
                    iconOnly
                    rounded
                    flat
                    onClick={() => {
                      setShowDeleteConfirmModal(true);
                      setIngestionId(ingestionListItem.id);
                    }}
                  >
                    <MaterialIcon id='cancel' center />
                  </StyledButton>
                )}
              </ListItemColumn>
            </ListItem>
          ))}
        </ListBody>
      </List>
      {showDeleteConfirmModal && (
        <ImportListItemDeleteConfirm
          handleDeleteIngestion={handleDeleteIngestion}
          onDismiss={onDismiss}
          showDeleteConfirmModal={showDeleteConfirmModal}
          supportType={supportType}
        />
      )}
    </>
  );
};

ImportList.propTypes = {
  ingestionList: PropTypes.array,
  projectService: projectServiceShape,
  currentUser: meShape,
};

const mapStateToProps = (state) => ({
  currentUser: selectCurrentMe(state),
});

export default compose(enhanceWithServices, withTheme, connect(mapStateToProps))(ImportList);
